/**
 * Chooses between a `supply*` (Org Design) GraphQL field and its legacy HRIS
 * equivalent for the reporting-line fields that coexist forever post Stage 3
 * (`managedBy`/`managing`/`managedByHierarchy`) — `managedBy` always resolves
 * from the legacy stored manager, `supplyManagedBy` always resolves from
 * supply-team ownership, regardless of workspace config. The workspace
 * `enableDemoODOptions` flag is what decides which one the UI should show;
 * it must be passed explicitly rather than inferred, because both fields are
 * now queried unconditionally almost everywhere, so "was the supply field
 * queried" is no longer a usable proxy for "is Org Design enabled here".
 *
 * When `useSupplyStructure` is true and the supply field was, unexpectedly,
 * not queried at all (key absent from the object), falls back to the legacy
 * field defensively rather than returning `undefined`.
 *
 * `supplyKey`/`legacyKey` are constrained to real keys of `obj` so a typo
 * (e.g. "supplyOwnerof") is a compile error rather than a silent, permanent
 * fallback to legacy data.
 *
 * Not for `owners`/`ownerOf` → `supplyOwners`/`supplyOwnerOf`: those were a
 * straight rename (the legacy fields are deleted from the schema), so there's
 * no flag-gated choice to make — read `supplyOwners`/`supplyOwnerOf` directly.
 */
export function preferSupplyField<
  Obj,
  Key extends Extract<keyof NonNullable<Obj>, string>,
>(
  obj: Obj,
  supplyKey: Key,
  legacyKey: Key,
  useSupplyStructure: boolean
): NonNullable<Obj>[Key] | undefined {
  if (!obj || typeof obj !== "object") {
    return undefined;
  }
  if (!useSupplyStructure) {
    return (obj as NonNullable<Obj>)[legacyKey];
  }
  if (supplyKey in obj) {
    return (obj as NonNullable<Obj>)[supplyKey];
  }
  return (obj as NonNullable<Obj>)[legacyKey];
}
